import * as React from 'react'
import { ReactElement } from 'react'
import { Avatar, makeStyles, MessageBar, MessageBarBody, Text, tokens, Tooltip } from '@fluentui/react-components'
import { BuildingBankRegular, HomeRegular, LocationRegular } from '@fluentui/react-icons'
import Loading from '../../../components/Loading'
import { isKvkError, KvkErrorResponse } from '../../Dialog/ChamberOfCommerce/KvkSearchDialog'
import { AxiosError } from 'axios'
import { DataPromise } from '../../../context/AxiosContext'
import { KvkSearchResult } from '../../../v2/helpers/kvkMappingHelper'
import { useTranslation } from 'react-i18next'

// A square, soort-coloured avatar per resultaattype; the colour is seeded on the type so every row of a type matches.
const typeIcons: Record<string, ReactElement> = {
    rechtspersoon: <BuildingBankRegular />,
    hoofdvestiging: <HomeRegular />,
    nevenvestiging: <LocationRegular />,
}

const useStyles = makeStyles({
    list: {
        display: 'flex',
        flexDirection: 'column',
        rowGap: tokens.spacingVerticalXXS,
    },
    row: {
        display: 'flex',
        alignItems: 'center',
        columnGap: tokens.spacingHorizontalM,
        padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalS}`,
        borderRadius: tokens.borderRadiusMedium,
        cursor: 'pointer',
        ':hover': { backgroundColor: tokens.colorNeutralBackground1Hover },
        ':active': { backgroundColor: tokens.colorNeutralBackground1Pressed },
        ':focus-visible': { outline: `${tokens.strokeWidthThick} solid ${tokens.colorStrokeFocus2}` },
    },
    nameBlock: {
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        flexGrow: 1,
    },
    name: {
        fontWeight: tokens.fontWeightSemibold,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    meta: {
        color: tokens.colorNeutralForeground3,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
})

type Props = {
    items: DataPromise<KvkSearchResult[], undefined, KvkErrorResponse>
    onSelect: (item: KvkSearchResult) => void
}

const parseError = (translate: (key: string) => string, error: AxiosError<KvkErrorResponse>): string =>
    isKvkError(error)
        ? error.response.data.fout.map(e => translate(`party.edit.coc.search.error.${e.code.toLowerCase()}`) ?? e.omschrijving).join(', ')
        : translate('party.edit.coc.search.error.unknown')

export default function KvkSearchResultList(props: Props): ReactElement {
    const styles = useStyles()
    const { t: translate } = useTranslation()

    if (props.items.loading) {
        return <Loading />
    }

    if (props.items.rejected) {
        return (
            <MessageBar intent={'error'} layout={'multiline'}>
                <MessageBarBody>{parseError(translate, props.items?.error)}</MessageBarBody>
            </MessageBar>
        )
    }

    if (!props.items.loaded) {
        return (
            <MessageBar intent={'info'} layout={'multiline'}>
                <MessageBarBody>{translate('party.edit.coc.search.description')}</MessageBarBody>
            </MessageBar>
        )
    }

    return (
        <div className={styles.list}>
            {props.items.data.map(item => {
                const typeLabel = translate(`party.edit.coc.type.${item.type}`)
                // Second line: KvK number, optional branch number, then the address, joined by middots.
                const meta = [
                    item.number,
                    item.branchNumber && `${translate('party.edit.coc.branchNumber')} ${item.branchNumber}`,
                    [item.street, item.locality].filter(Boolean).join(', '),
                ]
                    .filter(Boolean)
                    .join(' · ')

                return (
                    <div
                        key={`${item.number}-${item.branchNumber ?? ''}`}
                        className={styles.row}
                        role="button"
                        tabIndex={0}
                        onClick={() => props.onSelect(item)}
                        onKeyDown={event => {
                            if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault()
                                props.onSelect(item)
                            }
                        }}
                    >
                        <Tooltip relationship={'label'} content={typeLabel} withArrow>
                            <Avatar
                                shape={'square'}
                                color={'colorful'}
                                idForColor={item.type}
                                icon={typeIcons[item.type]}
                                aria-label={typeLabel}
                                size={32}
                            />
                        </Tooltip>
                        <div className={styles.nameBlock}>
                            <Text className={styles.name}>{item.name}</Text>
                            {meta && (
                                <Text size={200} className={styles.meta}>
                                    {meta}
                                </Text>
                            )}
                        </div>
                    </div>
                )
            })}
        </div>
    )
}
